<template>
    <div class="ai-markdown" v-html="html"></div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { renderMarkdown } from "@/utils/markdown";

const props = defineProps<{ content: string }>();

const html = computed(() => renderMarkdown(props.content));
</script>

<style>
/* Not scoped: v-html children carry no scope attribute, and every rule is
   namespaced under the unique .ai-markdown class instead. */
.ai-markdown > :first-child {
    margin-top: 0;
}

.ai-markdown > :last-child {
    margin-bottom: 0;
}

.ai-markdown p {
    margin: 0 0 0.5rem;
}

.ai-markdown h1,
.ai-markdown h2,
.ai-markdown h3,
.ai-markdown h4,
.ai-markdown h5,
.ai-markdown h6 {
    margin: 0.75rem 0 0.35rem;
    font-weight: 600;
    line-height: 1.35;
}

.ai-markdown h1 {
    font-size: 1.05rem;
}

.ai-markdown h2 {
    font-size: 1rem;
}

.ai-markdown h3,
.ai-markdown h4,
.ai-markdown h5,
.ai-markdown h6 {
    font-size: 0.925rem;
}

.ai-markdown ul,
.ai-markdown ol {
    margin: 0.25rem 0 0.5rem;
    padding-left: 1.25rem;
}

.ai-markdown ul {
    list-style: disc;
}

.ai-markdown ol {
    list-style: decimal;
}

.ai-markdown li {
    margin: 0.15rem 0;
}

.ai-markdown li > ul,
.ai-markdown li > ol {
    margin: 0.15rem 0 0.1rem;
}

.ai-markdown a {
    color: #2563eb;
    text-decoration: underline;
    text-underline-offset: 2px;
}

.dark .ai-markdown a {
    color: #93c5fd;
}

.ai-markdown code {
    padding: 0.1rem 0.3rem;
    border-radius: 0.25rem;
    background: rgb(0 0 0 / 0.07);
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.82em;
}

.dark .ai-markdown code {
    background: rgb(255 255 255 / 0.12);
}

.ai-markdown pre {
    margin: 0.5rem 0;
    padding: 0.75rem 0.9rem;
    border-radius: 0.5rem;
    background: #1f2937;
    color: #f3f4f6;
    overflow-x: auto;
    font-size: 0.8rem;
    line-height: 1.55;
}

.ai-markdown pre code {
    padding: 0;
    background: transparent;
    color: inherit;
    font-size: inherit;
}

.ai-markdown blockquote {
    margin: 0.5rem 0;
    padding-left: 0.75rem;
    border-left: 3px solid rgb(107 114 128 / 0.45);
    opacity: 0.9;
}

.ai-markdown table {
    display: block;
    width: 100%;
    margin: 0.5rem 0;
    overflow-x: auto;
    border-collapse: collapse;
    font-size: 0.8rem;
}

.ai-markdown th,
.ai-markdown td {
    padding: 0.3rem 0.55rem;
    border: 1px solid rgb(107 114 128 / 0.4);
    text-align: left;
}

.ai-markdown th {
    font-weight: 600;
    background: rgb(107 114 128 / 0.12);
}

.ai-markdown hr {
    margin: 0.75rem 0;
    border: 0;
    border-top: 1px solid rgb(107 114 128 / 0.35);
}
</style>
